// src/components/simulation/PhishingEmailSimulator.jsx - GMAIL-STYLED VERSION
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Shield, AlertTriangle, CheckCircle, Clock, ArrowLeft, ArrowRight, BookOpen,
  Inbox, Send, FileText, Trash2, AlertOctagon, Star, Search, Settings, GripVertical, UserCircle,
  Archive, MoreVertical, Reply, Forward, Target, Pencil, X
} from 'lucide-react';

const PhishingEmailSimulator = ({ emails = [], onComplete, difficulty = 'medium', challenge }) => {
  // --- STATE AND LOGIC ---
  const [currentEmailIndex, setCurrentEmailIndex] = useState(0);
  const [decisions, setDecisions] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [simulationCompleted, setSimulationCompleted] = useState(false);
  const [startTime] = useState(Date.now());
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackShown, setFeedbackShown] = useState(false);
  const navigate = useNavigate();


  const validEmails = emails && emails.length > 0 ? emails : '';
  const currentEmail = validEmails[currentEmailIndex];

  useEffect(() => {
    // console.log('🎯 PhishingEmailSimulator initialized with', validEmails.length, 'emails');
  }, [validEmails.length]);

  const handleEmailAction = (action) => {
    if (feedbackShown) return;

    const isCorrectAction = determineCorrectAction(currentEmail, action);
    const emailDecision = {
      emailId: currentEmail.id,
      action: action,
      isCorrect: isCorrectAction,
      timeSpent: Date.now() - startTime,
      email: currentEmail
    };

    setDecisions(prev => ({ ...prev, [currentEmail.id]: emailDecision }));
    setFeedbackShown(true);
    setShowFeedbackModal(true);
  };

  const determineCorrectAction = (email, action) => {
    if (email.isPhishing) {
      return ['delete', 'report', 'ignore'].includes(action);
    } else {
      return ['read', 'reply', 'forward'].includes(action);
    }
  };

  const moveToNextEmail = () => {
    setShowFeedbackModal(false);
    setFeedbackShown(false);

    if (currentEmailIndex < validEmails.length - 1) {
      setCurrentEmailIndex(prev => prev + 1);
    } else {
      completeSimulation();
    }
  };

  const completeSimulation = () => {
    const endTime = Date.now();
    const totalTime = Math.floor((endTime - startTime) / 1000);
    const allDecisions = Object.values(decisions);
    const correctCount = allDecisions.filter(d => d.isCorrect).length;
    const summary = {
      total: validEmails.length,
      correct: correctCount,
      incorrect: allDecisions.length - correctCount,
      score: Math.round((correctCount / validEmails.length) * 100),
      completionTime: totalTime,
      decisions: decisions,
      difficulty: difficulty
    };

    setSimulationCompleted(true);
    setShowResults(true);

    if (onComplete && typeof onComplete === 'function') {
      onComplete(summary);
    }
  };

  // MODIFICATION: Updated handleLearnMore function to check challenge metadata first
  const handleLearnMore = () => {
    const meta = challenge?.metadata;

    // Support older challenges where metadata was a plain URL string
    if (typeof meta === 'string' && meta.startsWith('http')) {
      window.open(meta, '_blank', 'noopener,noreferrer');
      return;
    }

    // Support newer JSON metadata objects with explicit URLs
    if (meta && typeof meta === 'object') {
      const url =
        meta.learnMoreUrl ||
        meta.learn_more_url ||
        meta.docUrl ||
        meta.doc_url ||
        meta.url;

      if (typeof url === 'string' && url.startsWith('http')) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    // 1. First check if challenge metadata has a learnMoreLink (legacy support)
    if (challenge?.metadata) {
      window.open(challenge.metadata, '_blank', 'noopener,noreferrer');
      return;
    }

    // 2. Check if current email has a learnMoreLink (existing functionality)
    if (currentEmail?.metadata) {
      window.open(currentEmail.metadata, '_blank', 'noopener,noreferrer');
      return;
    }

    // 3. Fallback to navigation (existing fallback)
    const arenaId = currentEmail?.arenaId || challenge?.category || 'phishing_social_engineering';
    if (arenaId) {
      navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
    }
  };

  // --- NEW HELPER FUNCTION ---
  /**
   * Formats the correctAction string from the database for display.
   * e.g., 'report_suspicious' becomes 'Report suspicious'.
   * Falls back to a default if the field is not present.
   */
  const formatCorrectAction = (email) => {
    if (!email) return '';
    const action = email.correctAction;
    if (!action) return email.isPhishing ? 'Report' : 'Read & Keep';
    return action.charAt(0).toUpperCase() + action.slice(1).replace(/_/g, ' ');
  };


  // --- RENDER FUNCTIONS (MODIFIED FOR GMAIL LOOK AND FEEL) ---

  const renderHeader = () => (
    <div className="flex items-center justify-between px-2 sm:px-4 h-14 sm:h-16 bg-gray-50 border-b border-gray-200 gap-2">
      <div className="flex items-center space-x-4">
        <img src="https://ssl.gstatic.com/ui/v1/icons/mail/rfr/logo_gmail_lockup_default_1x_r5.png" alt="Gmail Logo" className="h-8 sm:h-10" />
      </div>
      <div className="hidden md:block flex-1 max-w-2xl">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
          <input
            type="text"
            placeholder="Search in mail (disabled for simulation)"
            className="w-full bg-gray-200 rounded-full py-2.5 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
            disabled
          />
        </div>
      </div>
      <div className="flex items-center space-x-1 sm:space-x-4">
        {/* <span className="text-sm text-gray-600 flex items-center"><Clock size={14} className="mr-1.5" /> {Math.floor((Date.now() - startTime) / 1000)}s</span> */}
        <button className="hidden sm:inline-flex p-2 rounded-full hover:bg-gray-200 text-gray-600"><Settings size={20} /></button>
        <button className="hidden sm:inline-flex p-2 rounded-full hover:bg-gray-200 text-gray-600"><GripVertical size={20} /></button>
        <button className="p-1.5 sm:p-2 rounded-full hover:bg-gray-200 text-gray-600"><UserCircle size={22} /></button>
      </div>
    </div>
  );

  const renderSidebar = () => (
    <div className="hidden md:flex w-64 bg-gray-50 flex-col pt-4">
      <div className="px-2 mb-4">
        <button className="flex items-center justify-center w-full bg-white shadow-sm hover:shadow-md transition-shadow rounded-full p-4 space-x-3">
          <Pencil size={20} />
          <span className="font-medium text-sm">Compose</span>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {/* Gmail-style Folder List */}
        <div className="p-2 space-y-1">
          <a href="#" className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-r-full font-bold">
            <Inbox size={20} className="mr-4" />
            <span className="flex-1 text-sm">Inbox</span>
            <span className="text-xs">{validEmails.length}</span>
          </a>
          <a href="#" className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-r-full">
            <Star size={20} className="mr-4" />
            <span className="text-sm">Starred</span>
          </a>
          <a href="#" className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-r-full">
            <Send size={20} className="mr-4" />
            <span className="text-sm">Sent</span>
          </a>
          <a href="#" className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-r-full">
            <FileText size={20} className="mr-4" />
            <span className="text-sm">Drafts</span>
          </a>
          <a href="#" className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-r-full">
            <Trash2 size={20} className="mr-4" />
            <span className="text-sm">Trash</span>
          </a>
        </div>
        <div className="px-6 py-2 mt-4">
          <div className="text-xs text-gray-500 mb-1">
            Email {currentEmailIndex + 1} of {validEmails.length}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${((currentEmailIndex + 1) / validEmails.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFeedbackModal = () => {
    if (!showFeedbackModal) return null;

    const decision = decisions[currentEmail.id];
    const isCorrect = decision?.isCorrect;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-4 sm:p-8 m-4 animate-fade-in-up">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              {isCorrect ? (
                <CheckCircle size={28} className="text-green-600" />
              ) : (
                <AlertTriangle size={28} className="text-red-600" />
              )}
              <h2 className={`text-2xl font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                {isCorrect ? 'Correct Decision!' : 'Incorrect Decision!'}
              </h2>
            </div>
            <button onClick={moveToNextEmail} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          <div className="text-gray-700 space-y-4">
            {/* MODIFICATION START */}
            <p className="text-base">{currentEmail?.feedback || currentEmail?.explanation}</p>

            {/* Always show indicators for phishing emails */}
            {currentEmail.isPhishing && currentEmail.indicators?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="font-bold text-yellow-800 flex items-center">
                  <AlertTriangle size={16} className="mr-2" />
                  Key Indicators Found:
                </h3>
                <ul className="list-disc list-inside space-y-1.5 mt-2 text-gray-700 text-sm pl-2">
                  {currentEmail.indicators.map((indicator, index) => (
                    <li key={index}>{indicator}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Show the correct action only if the user was wrong */}
            {!isCorrect && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="font-bold text-green-800">Recommended Action:</h3>
                <p className="text-gray-700 mt-1">
                  This email was {currentEmail.isPhishing ? 'phishing' : 'legitimate'}. The best action was to '{formatCorrectAction(currentEmail)}'.
                </p>
              </div>
            )}
            {/* MODIFICATION END */}
          </div>

          {/* Modal Footer with Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row justify-end items-center gap-3 sm:gap-4">
            <button
              onClick={handleLearnMore}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <BookOpen size={16} />
              Learn More
            </button>
            <button
              onClick={moveToNextEmail}
              className="w-full sm:w-auto px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };
  const renderEmailContent = () => (
    <div className="flex-1 flex flex-col bg-white md:rounded-tl-2xl shadow-sm overflow-hidden min-w-0">
      {/* Email Toolbar */}
      <div className="flex items-center justify-between border-b border-gray-200 px-2 sm:px-4 py-2 gap-2 min-w-0">
        <div className="flex items-center space-x-1 min-w-0 overflow-x-auto">
          <button className="p-2 hover:bg-gray-100 rounded-full text-gray-600" title="Back"><ArrowLeft size={18} /></button>
          <button className="hidden sm:inline-flex p-2 hover:bg-gray-100 rounded-full text-gray-600" title="Archive"><Archive size={18} /></button>
          <button className="p-2 hover:bg-gray-100 rounded-full text-gray-600" title="Report Spam"><AlertOctagon size={18} /></button>
          <button className="p-2 hover:bg-gray-100 rounded-full text-gray-600" title="Delete"><Trash2 size={18} /></button>
        </div>
        <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-600 shrink-0">
          <span>{currentEmailIndex + 1} of {validEmails.length}</span>
          <button
            onClick={() => setCurrentEmailIndex(p => Math.max(0, p - 1))}
            disabled={currentEmailIndex === 0 || feedbackShown}
            className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <ArrowLeft size={16} />
          </button>
          <button
            onClick={() => setCurrentEmailIndex(p => Math.min(validEmails.length - 1, p + 1))}
            disabled={currentEmailIndex === validEmails.length - 1 || feedbackShown}
            className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <ArrowRight size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-6 min-w-0">
        {/* Email Header */}
        <h1 className="text-lg sm:text-2xl font-normal text-gray-900 mb-4 sm:mb-6 break-words">{currentEmail?.subject || 'No Subject'}</h1>
        <div className="flex items-start gap-3 sm:space-x-4 mb-6 sm:mb-8 min-w-0">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
            {currentEmail?.from?.name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 min-w-0">
              <span className="font-medium text-gray-800 truncate">{currentEmail?.from?.name || 'Unknown Sender'}</span>
              <span className="text-gray-500 text-xs sm:text-sm truncate">&lt;{currentEmail?.from?.email || 'no-reply@unknown.com'}&gt;</span>
            </div>
            <div className="text-xs sm:text-sm text-gray-600">to me</div>
          </div>
          <div className="text-xs sm:text-sm text-gray-500 flex items-center space-x-1 sm:space-x-4 shrink-0">
            <span className="hidden sm:inline">{new Date(currentEmail?.date || Date.now()).toLocaleString()}</span>
            <button className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full"><Star size={18} /></button>
            <button className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full"><MoreVertical size={18} /></button>
          </div>
        </div>

        {/* Email Body */}
        <div className="prose max-w-none">
          <div className="whitespace-pre-wrap break-words text-gray-800 leading-relaxed text-sm sm:text-base font-sans">
            {currentEmail?.body || 'This email has no content.'}
          </div>
        </div>

        {/* Reply/Forward Buttons */}
        {!feedbackShown && (
          <div className="mt-8 sm:mt-12 flex flex-wrap gap-2 sm:space-x-3">
            <button className="flex items-center space-x-2 border border-gray-300 rounded-full px-4 sm:px-5 py-2 hover:bg-gray-100 text-gray-700 text-sm font-medium">
              <Reply size={16} />
              <span>Reply</span>
            </button>
            <button className="flex items-center space-x-2 border border-gray-300 rounded-full px-4 sm:px-5 py-2 hover:bg-gray-100 text-gray-700 text-sm font-medium">
              <Forward size={16} />
              <span>Forward</span>
            </button>
          </div>
        )}
      </div>

      {/* Action Bar & Feedback */}
      <div className="border-t border-gray-200 bg-white p-3 sm:p-4">
        {feedbackShown ? (
          <div className={`p-4 rounded-lg ${decisions[currentEmail.id]?.isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex items-start space-x-3">
              {decisions[currentEmail.id]?.isCorrect ? <CheckCircle className="text-green-600 mt-0.5" size={20} /> : <AlertTriangle className="text-red-600 mt-0.5" size={20} />}
              {/* MODIFICATION START */}
              <div>
                <p className={`font-semibold ${decisions[currentEmail.id]?.isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                  {decisions[currentEmail.id]?.isCorrect ? 'Correct Decision!' : 'Incorrect Decision!'}
                </p>
                <p className="text-sm text-gray-700 mt-1">{currentEmail?.feedback || currentEmail?.explanation}</p>

                {/* Always show indicators for phishing emails */}
                {currentEmail.isPhishing && currentEmail.indicators?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <h5 className="font-bold text-sm text-yellow-800 flex items-center">
                      <AlertTriangle size={14} className="mr-1.5" />
                      Key Indicators:
                    </h5>
                    <ul className="list-disc list-inside space-y-1 mt-1 text-sm text-gray-700 pl-1">
                      {currentEmail.indicators.map((indicator, index) => (
                        <li key={index}>{indicator}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Show the correct action only if the user was wrong */}
                {!decisions[currentEmail.id]?.isCorrect && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <h5 className="font-bold text-sm text-green-800">Recommended Action:</h5>
                    <p className="text-sm text-gray-700 mt-1">
                      This email was {currentEmail.isPhishing ? 'phishing' : 'legitimate'}. The best action was to '{formatCorrectAction(currentEmail)}'.
                    </p>
                  </div>
                )}
              </div>
              {/* MODIFICATION END */}
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-base font-medium text-gray-800 mb-3 flex items-center">
              <Target className="mr-2 text-blue-600" /> What is the correct action?
            </h3>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
              {[
                { action: 'read', label: 'Read & Keep' }, { action: 'reply', label: 'Reply' }, { action: 'forward', label: 'Forward' },
                { action: 'delete', label: 'Delete' }, { action: 'report', label: 'Report Spam' }, { action: 'ignore', label: 'Ignore' }
              ].map(({ action, label }) => (
                <button
                  key={action}
                  onClick={() => handleEmailAction(action)}
                  className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm font-medium border rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500
                                bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderResults = () => {
    const correctCount = Object.values(decisions).filter(d => d.isCorrect).length;
    const score = Math.round((correctCount / validEmails.length) * 100);

    return (
      <div className="bg-white p-12 max-w-2xl mx-auto rounded-2xl shadow-xl">
        <div className="text-center mb-8">
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${score >= 80 ? 'bg-green-100' : score >= 60 ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
            {score >= 80 ? (
              <CheckCircle size={40} className="text-green-600" />
            ) : score >= 60 ? (
              <Shield size={40} className="text-yellow-600" />
            ) : (
              <AlertTriangle size={40} className="text-red-600" />
            )}
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Training Complete!</h2>
          <p className="text-gray-600">
            {score >= 80 ? 'Excellent security awareness! 🎉' :
              score >= 60 ? 'Good job, keep improving! 👍' :
                'Keep practicing to stay secure! 💪'}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="text-center">
            <div className="bg-green-50 p-4 rounded-xl mb-2">
              <div className="text-3xl font-bold text-green-600">{correctCount}</div>
            </div>
            <div className="text-sm font-medium text-gray-700">Correct</div>
          </div>
          <div className="text-center">
            <div className="bg-red-50 p-4 rounded-xl mb-2">
              <div className="text-3xl font-bold text-red-600">{validEmails.length - correctCount}</div>
            </div>
            <div className="text-sm font-medium text-gray-700">Incorrect</div>
          </div>
          <div className="text-center">
            <div className="bg-blue-50 p-4 rounded-xl mb-2">
              <div className="text-3xl font-bold text-blue-600">{score}%</div>
            </div>
            <div className="text-sm font-medium text-gray-700">Score</div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Email Analysis Summary</h3>
          <div className="space-y-3">
            {validEmails.map((email) => {
              const decision = decisions[email.id];
              return (
                <div key={email.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">{email.subject}</div>
                    <div className="text-xs text-gray-500">
                      {email.isPhishing ? '🚨 Phishing Email' : '✅ Legitimate Email'}
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${decision?.isCorrect
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                    }`}>
                    {decision?.isCorrect ? 'Correct' : 'Incorrect'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-center">
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
            >
              Return to Training
            </button>
          </div>
        </div>
      </div>
    );
  };

  // --- MAIN RENDER ---
  return (
    <div className="fixed inset-0 bg-gray-100 text-gray-900 flex flex-col font-sans">
      {renderHeader()}
      <div className="flex-1 flex overflow-hidden">
        {renderSidebar()}
        <main className="flex-1 flex flex-col p-2 sm:p-4 md:pl-0">
          {showResults ? (
            <div className="flex-1 flex items-center justify-center">
              {renderResults()}
            </div>
          ) : (
            renderEmailContent()
          )}
        </main>
        {renderFeedbackModal()}
      </div>
    </div>
  );
};

export default PhishingEmailSimulator;